import { useCallback, useEffect, useMemo, useState } from "react";
import { useArtistScope } from "@/lib/artistContext";
import {
  connectShopifyShop,
  journalizeShopifyPayout,
  listShopifyOrders,
  listShopifyPayouts,
  listShopifyShops,
  pullShopifyOrders,
  pullShopifyPayouts,
} from "@/api/shopify";
import type { ShopifyPayout, ShopifyShop, ShopifyOrder } from "@/types/shopify";

export function useShopify() {
  const { selectedArtistId } = useArtistScope();
  const [shops, setShops] = useState<ShopifyShop[]>([]);
  const [payouts, setPayouts] = useState<ShopifyPayout[]>([]);
  const [orders, setOrders] = useState<ShopifyOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingShop, setSavingShop] = useState(false);
  const [postingPayoutId, setPostingPayoutId] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    if (!selectedArtistId) {
      setShops([]);
      setPayouts([]);
      setOrders([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [shopsPayload, payoutsPayload, ordersPayload] = await Promise.all([
        listShopifyShops(),
        listShopifyPayouts(),
        listShopifyOrders(),
      ]);
      setShops(shopsPayload ?? []);
      setPayouts(payoutsPayload ?? []);
      setOrders(ordersPayload ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load Shopify data");
    } finally {
      setLoading(false);
    }
  }, [selectedArtistId]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const saveShop = useCallback(
    async (payload: Parameters<typeof connectShopifyShop>[0]) => {
      setSavingShop(true);
      try {
        await connectShopifyShop(payload);
        await loadAll();
      } finally {
        setSavingShop(false);
      }
    },
    [loadAll],
  );

  const refreshPayouts = useCallback(async () => {
    await loadAll();
  }, [loadAll]);

  const syncPayouts = useCallback(
    async (shop_id: string) => {
      await pullShopifyPayouts(shop_id);
      await loadAll();
    },
    [loadAll],
  );

  const syncOrders = useCallback(
    async (shop_id: string, created_after?: string) => {
      await pullShopifyOrders(shop_id, created_after);
      await loadAll();
    },
    [loadAll],
  );

  const journalizePayout = useCallback(
    async (payout_id: string, journal_id: number) => {
      setPostingPayoutId(payout_id);
      try {
        await journalizeShopifyPayout(payout_id, journal_id);
        await refreshPayouts();
      } finally {
        setPostingPayoutId(null);
      }
    },
    [refreshPayouts],
  );

  return useMemo(
    () => ({
      shops,
      payouts,
      orders,
      loading,
      error,
      savingShop,
      postingPayoutId,
      saveShop,
      syncPayouts,
      syncOrders,
      journalizePayout,
      refresh: loadAll,
    }),
    [
      shops,
      payouts,
      orders,
      loading,
      error,
      savingShop,
      postingPayoutId,
      saveShop,
      syncPayouts,
      syncOrders,
      journalizePayout,
      loadAll,
    ],
  );
}
